import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  scaleTime,
  scaleLinear,
  extent,
  max,
  axisBottom,
  axisLeft,
  select,
  format
} from "d3";
import Area from "./Area";
import Tooltip from "./Tooltip";

export default function CompareGraph({
  className,
  title,
  firstData,
  secondData,
}: {
  className: string;
  title: string;
  firstData: any;
  secondData: any;
}) {
  const svgRef = useRef(null);
  const overlayRef = useRef(null);
  const tooltipRef = useRef(null);
  const [width, setWidth] = useState(700);
  const [height, setHeight] = useState(400);

  const margin = useMemo(
    () => ({ left: 1, right: 20, top: 12, bottom: 10 }),
    []
  );

  const minorTickSize = 10;
  const allowance = 24;
  const bottom = height - margin.bottom - minorTickSize - 14;

  const data = useMemo(
    () =>
      firstData && secondData
        ? [
            {
              borderColor: "#5676e9",
              backgroundColor: "#5676e9",
              ...firstData,
            },
            {
              borderColor: "#4bc5dc",
              backgroundColor: "#4bc5dc",
              ...secondData,
            },
          ]
        : [],
    [firstData, secondData]
  );

  const xAccessor = (d: any) => {
    const date = new Date(d.timestamp);
    date.setHours(0, 0, 0);

    return date;
  };

  const yAccessor = (d: any) => parseFloat(d.value);

  const xScale = useMemo(
    () =>
      scaleTime()
        .domain(
          extent(
            firstData && secondData
              ? [...firstData.data, ...secondData.data]
              : [],
            xAccessor
          )
        )
        .range([minorTickSize + margin.left + allowance, width - margin.right]),
    [firstData, secondData, width, margin]
  );

  const yScale = useMemo(
    () => {
      const maxValue = max(firstData && secondData ? [...firstData.data, ...secondData.data] : [], yAccessor);

      return scaleLinear()
        .domain([0, maxValue > 200 ? maxValue + maxValue / 3 : 200])
        .nice()
        .range([bottom, margin.top]);
    },
    [firstData, secondData, bottom, margin]
  );

  const drawAxis = useCallback(
    ({
      container,
      xScale,
      yScale,
      tickArguments,
      tickSize,
      tickFormat,
      color,
      transform,
      noText,
    }: any) => {
      const scale = xScale || yScale;
      const axisType = xScale ? axisBottom : axisLeft;
      const axis = axisType(scale)
        .tickArguments(tickArguments)
        .tickSize(tickSize)
        .tickFormat(tickFormat);
      const axisGroup = container
        .append("g")
        .attr("class", "axis")
        .attr("color", color)
        .attr("transform", transform)
        .call(axis);
      axisGroup
        .selectAll("line")
        .attr("stroke", color)
        .attr("stroke-width", "0.5");
      axisGroup
        .selectAll("text")
        .attr("color", "#95A0B7")
        // .attr("transform", "rotate(-45)")
        // .attr("transform-origin", "0 20")
        .attr("font-size", "0.75rem");
      if (noText) {
        axisGroup.selectAll("text").remove();
      }
    },
    []
  );

  useEffect(() => {
    // Create root container where we will append all other chart elements
    const svgEl = select(svgRef.current).select(".axis-group");
    svgEl.selectAll(".axis").remove();
    // Add X grid lines with labels
    drawAxis({
      xScale,
      container: svgEl,
      color: "rgb(149 160 183)",
      tickSize: 0,
      tickArguments: [1, "%b %d"],
      transform: `translate(0, ${height - margin.bottom + margin.top - minorTickSize - 5})`,
    });

    // Add Y grid lines with labels
    drawAxis({
      yScale,
      container: svgEl,
      color: "rgb(149 160 183)",
      tickSize: 0,
      tickArguments: [1, "~s"],
      transform: `translate(${margin.left + minorTickSize + allowance}, ${margin.top})`,
      tickFormat: (val: any) => `€${format("~s")(val)}`,
    });
    // Add Y grid lines without labels
    drawAxis({
      yScale,
      container: svgEl,
      noText: true,
      color: "rgb(149, 160, 183, 0.2)",
      tickSize: margin.right - width + allowance + minorTickSize,
      tickArguments: [5, "~s"],
      transform: `translate(${margin.left + minorTickSize + allowance}, ${margin.top})`,
      // tickFormat: (val: any) => `${val}%`,
    });
    svgEl.selectAll(".domain").remove();
  }, [drawAxis, width, height, margin, xScale, yScale]);

  const resizeWindow = () => {
    const rect = svgRef.current.getBoundingClientRect();
    setWidth(rect.width);
    setHeight(rect.height);
  };

  useEffect(() => {
    resizeWindow();
    window.addEventListener("resize", resizeWindow);
    return () => window.removeEventListener("resize", resizeWindow);
  }, []);

  return (
    <section className="relative mb-4">
      <div className="text-gray-500 font-bold mt-7 text-sm">{title}</div>
      <svg overflow="visible" ref={(node) => (node ? (svgRef.current = node) : null)} className={className + ' cursor-crosshair'} viewBox={`0 0 ${width} ${height}`}>
        <g className="axis-group" />
        <g className="chart">
          {data.map((item: any, index: number) => (
            <Area
              key={index}
              xScale={xScale}
              yScale={yScale}
              xAccessor={xAccessor}
              yAccessor={yAccessor}
              dotRaduis={3}
              color={item.backgroundColor}
              borderColor={item.borderColor}
              data={item.data}
              index={index}
              anchorEl={svgRef.current}
              tooltipEl={tooltipRef.current}
            />
          ))}
          <g className="dot-group" />
          <g>
            <Tooltip
              xScale={xScale}
              yScale={() => yScale}
              xAccessor={xAccessor}
              yAccessor={yAccessor}
              width={width}
              height={height}
              data={data}
              top={margin.top}
              bottom={bottom}
              anchorEl={overlayRef.current}
              format={(val: number) => val.toLocaleString("en-GB", { style: "currency", currency: "EUR" })}
            />
            <rect ref={overlayRef} width={width - margin.right} height={bottom} transform={`translate(${margin.left}, ${margin.top})`} opacity={0} />
          </g>
        </g>
      </svg>
      <div className="flex flex-wrap items-center gap-2 my-5 ml-5 text-xs">
        <div className="flex items-center">
          <div className="rounded-full bg-blue-600 w-4 h-4 my-1 mr-2"></div>
          <div className="shrink">{firstData?.title}</div>
        </div>
        <div className="flex items-center">
          <div className="rounded-full bg-cyan-600 w-4 h-4 my-1 mr-2"></div>
          <div className="shrink">{secondData?.title}</div>
        </div>
      </div>
    </section>
  );
}
